import {
	ApolloClient,
	ApolloLink,
	InMemoryCache,
	createHttpLink,
	split,
	Observable
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient, type Client } from 'graphql-ws';
import { print } from 'graphql';
import {
	GRAPHQL_DEFAULT_HEADERS
} from '@/providers/graphql/headers';
import {
	beginNetworkActivity,
	endNetworkActivity
} from '@/lib/network-activity';

export const API_URL = import.meta.env.VITE_API_URL;

// Auth error callback - will be set by AuthContext
let onAuthError: (() => void) | null = null;

export const setAuthErrorCallback = (callback: () => void) => {
	onAuthError = callback;
};

export const clearAuthErrorCallback = () => {
	onAuthError = null;
};

// Derive WebSocket URL from API URL
const getWsUrl = (httpUrl: string): string => {
	const url = new URL(httpUrl);
	url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
	return url.toString();
};

export const WS_URL = getWsUrl(API_URL);

const httpLink = createHttpLink({
	uri: API_URL,
	headers: GRAPHQL_DEFAULT_HEADERS,
	credentials: 'include' // Enable cookies for authentication
});

// Create WebSocket link for subscriptions
// The client is created lazily and can be reconnected when auth state changes
let wsClient: Client | null = null;
let wsLink: GraphQLWsLink | null = null;

export const createWsClient = () => {
	if (wsClient) {
		wsClient.dispose();
	}

	wsClient = createClient({
		url: WS_URL,
		connectionParams: () => {
			// Cookies are automatically sent with WebSocket connection
			return {};
		},
		retryAttempts: 5,
		shouldRetry: () => true,
		on: {
			connected: () => console.log('[WS] Connected to GraphQL subscriptions'),
			closed: () => console.log('[WS] Disconnected from GraphQL subscriptions'),
			error: (error) =>
				console.error('[WS] GraphQL subscription error:', error)
		}
	});

	wsLink = new GraphQLWsLink(wsClient);
	return wsClient;
};

export const disposeWsClient = () => {
	if (wsClient) {
		wsClient.dispose();
		wsClient = null;
		wsLink = null;
	}
};

export const getWsLink = (): GraphQLWsLink => {
	if (!wsLink) {
		createWsClient();
	}
	return wsLink!;
};

// Create a lazy link that delegates to the WebSocket link
// This allows us to create/dispose the WS connection based on auth state
const lazyWsLink = new ApolloLink((operation) => {
	return new Observable((observer) => {
		const link = getWsLink();
		const subscription = link.request(operation)?.subscribe({
			next: observer.next.bind(observer),
			error: observer.error.bind(observer),
			complete: observer.complete.bind(observer)
		});

		return () => {
			subscription?.unsubscribe();
		};
	});
});

// Error link to handle authentication errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
	if (graphQLErrors) {
		for (const error of graphQLErrors) {
			// Check for authentication error code in extensions
			const errorCode = (error.extensions as { code?: string })?.code;
			if (
				errorCode === 'AUTH_NOT_AUTHENTICATED' ||
				errorCode === 'UNAUTHENTICATED'
			) {
				console.warn('[Auth] Received authentication error, triggering logout');
				if (onAuthError) {
					onAuthError();
				}
				break;
			}
		}
	}

	// Also handle network-level 401 errors
	if (networkError && 'statusCode' in networkError) {
		const statusCode = (networkError as { statusCode?: number }).statusCode;
		if (statusCode === 401) {
			console.warn('[Auth] Received 401 network error, triggering logout');
			if (onAuthError) {
				onAuthError();
			}
		}
	}
});

// Tracks active query/mutation requests so detached windows can keep splash
// visible until the initial entity fetch has actually settled.
const networkActivityLink = new ApolloLink((operation, forward) => {
	if (!forward) {
		return null;
	}

	const definition = getMainDefinition(operation.query);
	const isSubscriptionOperation =
		definition.kind === 'OperationDefinition' &&
		definition.operation === 'subscription';

	if (isSubscriptionOperation) {
		return forward(operation);
	}

	beginNetworkActivity();
	let didFinalize = false;

	const finalize = () => {
		if (didFinalize) {
			return;
		}
		didFinalize = true;
		endNetworkActivity();
	};

	return new Observable(observer => {
		const subscription = forward(operation).subscribe({
			next: value => observer.next(value),
			error: error => {
				finalize();
				observer.error(error);
			},
			complete: () => {
				finalize();
				observer.complete();
			}
		});

		return () => {
			finalize();
			subscription.unsubscribe();
		};
	});
});

// Split link: send subscriptions to WebSocket, queries/mutations to HTTP
const splitLink = split(
	({ query }) => {
		const definition = getMainDefinition(query);
		return (
			definition.kind === 'OperationDefinition' &&
			definition.operation === 'subscription'
		);
	},
	lazyWsLink,
	httpLink
);

const tryFormatJson = (value: unknown): string | null => {
	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return null;
	}
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const logJson = (label: string, value: unknown) => {
	const formatted = tryFormatJson(value);
	if (formatted) {
		console.log(`${label}:\n${formatted}`);
		return;
	}

	console.log(`${label}:`, value);
};

const getResponseSummary = (value: unknown): Record<string, unknown> | null => {
	if (!isObjectRecord(value)) {
		return null;
	}

	const summary: Record<string, unknown> = {};

	for (const key of ['ok', 'status', 'statusText', 'url', 'type', 'redirected']) {
		if (key in value) {
			summary[key] = value[key];
		}
	}

	return Object.keys(summary).length > 0 ? summary : null;
};

const logTransportPayload = (label: string, value: unknown) => {
	if (!isObjectRecord(value)) {
		return;
	}

	const errors = value.errors;
	if (Array.isArray(errors) && errors.length > 0) {
		logJson(`${label} Errors`, errors);
	}

	if (typeof value.statusCode !== 'undefined') {
		console.log(`${label} Status Code:`, value.statusCode);
	}

	if (typeof value.result !== 'undefined') {
		logJson(`${label} Result`, value.result);
	}

	if (typeof value.bodyText === 'string' && value.bodyText.length > 0) {
		console.log(`${label} Body:\n${value.bodyText}`);
	}

	const responseSummary = getResponseSummary(value.response);
	if (responseSummary) {
		logJson(`${label} Response`, responseSummary);
	}
};

const logErrorDetails = (error: unknown) => {
	console.error('Error:', error);
	logTransportPayload('Error', error);

	if (!isObjectRecord(error)) {
		return;
	}

	const graphQLErrors = error.graphQLErrors;
	if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
		logJson('GraphQL Errors', graphQLErrors);
	}

	const clientErrors = error.clientErrors;
	if (Array.isArray(clientErrors) && clientErrors.length > 0) {
		logJson('Client Errors', clientErrors);
	}

	const protocolErrors = error.protocolErrors;
	if (Array.isArray(protocolErrors) && protocolErrors.length > 0) {
		logJson('Protocol Errors', protocolErrors);
	}

	if (typeof error.extraInfo !== 'undefined') {
		logJson('Extra Info', error.extraInfo);
	}

	if (typeof error.networkError !== 'undefined') {
		console.error('Network Error:', error.networkError);
		logTransportPayload('Network Error', error.networkError);
	}
};

const logGraphqlEntry = ({
	operationType,
	operationName,
	queryText,
	variables,
	duration,
	response,
	error,
	emissionCount
}: {
	operationType: string;
	operationName: string;
	queryText: string;
	variables: Record<string, unknown>;
	duration: string;
	response?: unknown;
	error?: unknown;
	emissionCount?: number;
}) => {
	const emissionSuffix =
		emissionCount && emissionCount > 1 ? ` #${emissionCount}` : '';

	console.groupCollapsed(
		`%c[GraphQL ${operationType}]%c ${operationName}${emissionSuffix}`,
		'color: #03A9F4; font-weight: bold',
		'color: inherit; font-weight: normal'
	);
	console.log('Query:\n' + queryText);
	logJson('Variables', variables);

	if (typeof error !== 'undefined') {
		logErrorDetails(error);
	} else {
		logJson('Result', response);
		logTransportPayload('Result', response);
	}

	console.log(`Duration: ${duration}ms`);
	console.groupEnd();
};

// Lightweight dev-only GraphQL logger link (replaces apollo-link-logger which is incompatible with Apollo Client 3)
const loggerLink = new ApolloLink((operation, forward) => {
	if (!forward) {
		return null;
	}

	const { operationName } = operation;
	const definition = getMainDefinition(operation.query);
	const operationType =
		definition.kind === 'OperationDefinition' ? definition.operation : 'unknown';
	const startTime = performance.now();
	const queryText = print(operation.query);
	let emissionCount = 0;

	return new Observable(observer => {
		const subscription = forward(operation).subscribe({
			next: response => {
				emissionCount += 1;
				const duration = (performance.now() - startTime).toFixed(1);
				logGraphqlEntry({
					operationType,
					operationName,
					queryText,
					variables: operation.variables,
					duration,
					response,
					emissionCount
				});
				observer.next(response);
			},
			error: error => {
				const duration = (performance.now() - startTime).toFixed(1);
				logGraphqlEntry({
					operationType,
					operationName,
					queryText,
					variables: operation.variables,
					duration,
					error,
					emissionCount: emissionCount || undefined
				});
				observer.error(error);
			},
			complete: () => {
				observer.complete();
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	});
});

// Combine links — include logger only in development builds
const link = ApolloLink.from([
	...(import.meta.env.DEV ? [loggerLink] : []),
	networkActivityLink,
	errorLink,
	splitLink
]);

export const apolloClient = new ApolloClient({
	link: link,
	cache: new InMemoryCache({
		resultCaching: true
	}),
	// Default fetch policy for all queries
	defaultOptions: {
		query: {
			fetchPolicy: 'cache-first', // Use cache first, then network
			errorPolicy: 'all' // Return both data and errors
		},
		mutate: {
			errorPolicy: 'all'
		},
		watchQuery: {
			fetchPolicy: 'cache-and-network', // Use cache first, then network
			errorPolicy: 'all'
		}
	}
});
